import { TFunction } from 'i18next';
import {
  C_Besr_Format,
  C_Booking_Type,
  C_Ca_Visibility_Status,
  C_Correspondence_Language,
  C_Customer_Address_Kind,
  C_Customer_Def_Order_Source,
  C_Debitor_Status,
  C_Direct_Debit_Banks,
  C_Discounts_And_Prices_Kind,
  C_Discount_Kind,
  C_E_Invoice_Format,
  C_Freezer_Condition,
  C_Invoice_Interval,
  C_Kg_Representation,
  C_Procurement_Product_Kind,
  C_Report,
  C_Sale_Unit,
  C_Tax_System,
  C_Vat_Code,
  C_Weigth_Fraction_Type,
} from '../../../../../../../graphql/generatedModel';
// Type-only: keeps the service singleton (and with it the GraphQL client) out
// of this module's runtime graph, so the option builders can be exercised on
// their own.
import type { ImportTabOptionsRes } from '../../../../services/configuration.service';

/**
 * The option lists that come from the customer database. Until they arrive the
 * columns they feed have nothing to show — there is no local substitute for a
 * tour plan or a price category.
 */
export const initDictOptions = (data: ImportTabOptionsRes) => {
  return {
    customerGroups: prepareOptionsAnyDict(data.customerGroups),
    postSectors: prepareOptionsAnyDict(data.postSectors),
    quantityDiscounts: prepareOptionsAnyDict(data.quantityDiscounts),
    deliveryNoteGroups: prepareOptionsAnyDict(data.deliveryNoteGroups),
    priceCategories: prepareOptionsAnyDict(data.priceCategories),
    discountGroups: prepareOptionsAnyDict(data.discountGroups),
    marketingGroups: prepareOptionsAnyDict(data.marketingGroups),
    productionGroups: prepareOptionsAnyDict(data.productionGroups),
    shippingPoints: prepareOptionsAnyDict(data.shippingPoints),
    tourplans: prepareOptionsAnyDict(data.tourplans),
    banks: prepareOptionsAnyDict(data.banks),
    storageLocations: prepareOptionsAnyDict(data.storageLocations),
    caTemplates: prepareOptionsAnyDict(data.caTemplates),
    caProductGroups: prepareOptionsAnyDict(data.caProductGroups),
  };
};

/**
 * The option lists built entirely from generated enums and the label bundles —
 * no server call is involved. They are kept apart from the dictionary lists on
 * purpose: `importTabOptions` is a `zip` of fourteen dictionary queries, so a
 * single slow or failing dictionary used to leave every enum column of the
 * preview blank as well. The cells render `options.find(...)?.label`, so an
 * empty list is indistinguishable from an unrecognised value — a correctly
 * imported "Pièce" or "TVA taux normal" showed as nothing at all (WA-3846).
 * Seeding these at mount makes the enum columns independent of the fetch.
 */
export const initEnumOptions = (t: TFunction<'translation', undefined>) => {
  return {
    besrFormats: prepareOptionsAnyEnum(C_Besr_Format, t),
    bookingTypes: prepareOptionsAnyEnum(C_Booking_Type, t),
    visibilityStatuses: prepareOptionsAnyEnum(C_Ca_Visibility_Status, t),
    correspondenceLanguages: prepareOptionsAnyEnum(C_Correspondence_Language, t),
    customerAddressKinds: prepareOptionsAnyEnum(C_Customer_Address_Kind, t),
    customerDefOrderSources: prepareOptionsAnyEnum(C_Customer_Def_Order_Source, t),
    debitorStatuses: prepareOptionsAnyEnum(C_Debitor_Status, t),
    directDebitBanks: prepareOptionsAnyEnum(C_Direct_Debit_Banks, t),
    discountsAndPricesKinds: prepareOptionsAnyEnum(C_Discounts_And_Prices_Kind, t),
    discountKinds: prepareOptionsAnyEnum(C_Discount_Kind, t),
    eInvoiceFormats: prepareOptionsAnyEnum(C_E_Invoice_Format, t),
    freezerConditions: prepareOptionsAnyEnum(C_Freezer_Condition, t),
    invoiceIntervals: prepareOptionsAnyEnum(C_Invoice_Interval, t),
    kgRepresentations: prepareOptionsAnyEnum(C_Kg_Representation, t),
    procurementProductKinds: prepareOptionsAnyEnum(C_Procurement_Product_Kind, t),
    reports: prepareOptionsAnyEnum(C_Report, t),
    saleUnits: prepareOptionsAnyEnum(C_Sale_Unit, t),
    taxSystems: prepareOptionsAnyEnum(C_Tax_System, t),
    vatCodes: prepareOptionsAnyEnum(C_Vat_Code, t),
    weigthFractionTypes: prepareOptionsAnyEnum(C_Weigth_Fraction_Type, t),
  };
};

export const initOptionsArray = (
  data: ImportTabOptionsRes,
  t: TFunction<'translation', undefined>,
) => {
  return { ...initDictOptions(data), ...initEnumOptions(t) };
};

export const prepareOptionsAnyDict = (array: { id: any; label?: any; [key: string]: any }[]) => {
  return Object.values(array).map((key) => ({
    id: key.id,
    label: key.label,
  }));
};

export const prepareOptionsAnyEnum = (
  inEnum:
    | typeof C_Besr_Format
    | typeof C_Booking_Type
    | typeof C_Ca_Visibility_Status
    | typeof C_Correspondence_Language
    | typeof C_Customer_Address_Kind
    | typeof C_Customer_Def_Order_Source
    | typeof C_Debitor_Status
    | typeof C_Direct_Debit_Banks
    | typeof C_Discounts_And_Prices_Kind
    | typeof C_Discount_Kind
    | typeof C_E_Invoice_Format
    | typeof C_Freezer_Condition
    | typeof C_Invoice_Interval
    | typeof C_Kg_Representation
    | typeof C_Procurement_Product_Kind
    | typeof C_Report
    | typeof C_Sale_Unit
    | typeof C_Tax_System
    | typeof C_Vat_Code
    | typeof C_Weigth_Fraction_Type,
  t: TFunction<'translation', undefined>,
) => {
  return Object.values(inEnum).map((key) => ({
    id: key,
    label: t(`enums.${key}`),
  }));
};
